import React, { useCallback, useRef, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ANNOTATIONS_BASE_QUERY_KEY } from 'api/annotationAssignments';
import { bulkChangeAnnotationWorkingState, getSlideAnnotations, prepareAnnotationDraft } from 'api/annotations';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { filter, find, includes, map, size, some } from 'es-toolkit/compat';
import { Annotation, AnnotationWorkingStates } from 'interfaces/annotation';
import { enqueueSnackbar } from 'notistack';
import pluralize from 'pluralize';

import {
  ANNOTATION_ASSIGNMENT_DISPLAY_STATUS_LABELS,
  getTotalAnnotationsCount,
} from './AnnotationAssignmentTable.utils';

interface UseBulkAnnotationWorkingStateOptions {
  annotationAssignmentId: number;
  selectedRows: Annotation[];
  onCompleted: (completedAnnotationIds: number[]) => void;
}

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return 'Failed to change annotation working states';
};

const hasAnnotationMarkers = (annotation?: Annotation) =>
  some(annotation?.draftAnnotationsData, ({ markers }) => size(markers) > 0);

export const useBulkAnnotationWorkingState = ({
  annotationAssignmentId,
  selectedRows,
  onCompleted,
}: UseBulkAnnotationWorkingStateOptions) => {
  const queryClient = useQueryClient();
  const confirmWithModal = useConfirmation();
  const submissionInFlightRef = useRef(false);
  const [pendingTarget, setPendingTarget] = useState<AnnotationWorkingStates | null>(null);
  const mutation = useMutation({
    mutationFn: bulkChangeAnnotationWorkingState,
  });

  const submit = useCallback(
    async (targetWorkingState: AnnotationWorkingStates) => {
      if (submissionInFlightRef.current || mutation.isLoading) {
        return;
      }

      const unchangedRows = filter(selectedRows, ({ workingState }) => workingState === targetWorkingState);
      const changingRows = filter(selectedRows, ({ workingState }) => workingState !== targetWorkingState);
      const rowsThatCanRestoreFromPublishedAnnotations = filter(
        changingRows,
        ({ workingState }) => workingState === AnnotationWorkingStates.Done
      );
      const rowsThatRequireNonEmptyDrafts = filter(
        changingRows,
        ({ workingState }) => workingState !== AnnotationWorkingStates.Done
      );
      const emptyDraftRows = filter(
        rowsThatRequireNonEmptyDrafts,
        ({ draftAnnotationSummary }) => getTotalAnnotationsCount(draftAnnotationSummary) === 0
      );
      const eligibleRows = [
        ...rowsThatCanRestoreFromPublishedAnnotations,
        ...filter(
          rowsThatRequireNonEmptyDrafts,
          ({ draftAnnotationSummary }) => getTotalAnnotationsCount(draftAnnotationSummary) > 0
        ),
      ];

      if (emptyDraftRows.length > 0) {
        if (eligibleRows.length === 0) {
          await confirmWithModal({
            title: 'No selected slides can be changed',
            content: (
              <React.Fragment>
                Empty annotation data prevents changing {emptyDraftRows.length} selected{' '}
                {pluralize('slide', emptyDraftRows.length)}. Bulk actions never save or publish empty annotation data.
              </React.Fragment>
            ),
            confirmButtonProps: { sx: { display: 'none' } },
            cancelButtonProps: { title: 'Close' },
          });
          return;
        }

        const approved = await confirmWithModal({
          title: `Change ${eligibleRows.length} eligible ${pluralize('slide', eligibleRows.length)}?`,
          content: (
            <React.Fragment>
              Empty annotation data prevents changing {emptyDraftRows.length} selected{' '}
              {pluralize('slide', emptyDraftRows.length)}. Continue with only the {eligibleRows.length} non-empty{' '}
              {pluralize('slide', eligibleRows.length)}?
            </React.Fragment>
          ),
          confirmButtonProps: { title: `Change ${eligibleRows.length} eligible` },
          cancelButtonProps: { title: 'Cancel' },
        });

        if (!approved) {
          return;
        }
      }

      if (eligibleRows.length === 0) {
        return;
      }

      submissionInFlightRef.current = true;
      setPendingTarget(targetWorkingState);
      const rowsRequiringDraftPreparation = filter(
        eligibleRows,
        ({ draftVersionHash, workingState }) => workingState !== AnnotationWorkingStates.Done && !draftVersionHash
      );
      try {
        const preparationResults = await Promise.allSettled(
          map(rowsRequiringDraftPreparation, async (row) => {
            const slideAnnotations = await getSlideAnnotations({ slideId: row.slideId, includeEmpty: true });
            const hydratedAnnotation = find(
              slideAnnotations,
              ({ annotationAssignment }) => annotationAssignment?.annotationAssignmentId === annotationAssignmentId
            );

            if (!hydratedAnnotation || !hasAnnotationMarkers(hydratedAnnotation)) {
              throw new Error(`Unable to prepare the draft for slide ${row.slideId}`);
            }

            if (hydratedAnnotation.draftVersionHash) {
              return { ...row, draftVersionHash: hydratedAnnotation.draftVersionHash };
            }

            const response = await prepareAnnotationDraft({
              annotationAssignmentId,
              slideId: row.slideId,
            });
            if (!response.draftVersionHash) {
              throw new Error(`Unable to prepare the draft for slide ${row.slideId}`);
            }

            return { ...row, draftVersionHash: response.draftVersionHash };
          })
        );
        const failedPreparation = find(preparationResults, ({ status }) => status === 'rejected');
        if (failedPreparation?.status === 'rejected') {
          throw failedPreparation.reason;
        }
        const preparedRowsBySlideId = new Map(
          map(preparationResults, (result) => {
            if (result.status !== 'fulfilled') {
              throw result.reason;
            }
            return [result.value.slideId, result.value] as const;
          })
        );
        const preparedEligibleRows = map(eligibleRows, (row) => preparedRowsBySlideId.get(row.slideId) ?? row);

        const response = await mutation.mutateAsync({
          annotationAssignmentId,
          request: {
            workingState: targetWorkingState,
            slides: map(preparedEligibleRows, ({ slideId, draftVersionHash, workingState }) => ({
              slideId,
              ...(workingState === AnnotationWorkingStates.Done || !draftVersionHash ? {} : { draftVersionHash }),
            })),
          },
        });

        await queryClient.invalidateQueries({ queryKey: [ANNOTATIONS_BASE_QUERY_KEY] });

        const completedSlideIds = [...response.updatedSlideIds, ...response.unchangedSlideIds];
        onCompleted([
          ...map(
            filter(eligibleRows, ({ slideId }) => includes(completedSlideIds, slideId)),
            'annotationId'
          ),
          ...map(unchangedRows, 'annotationId'),
        ]);
        enqueueSnackbar(
          `${response.updatedSlideIds.length} ${pluralize('slide', response.updatedSlideIds.length)} changed to ${
            ANNOTATION_ASSIGNMENT_DISPLAY_STATUS_LABELS[targetWorkingState]
          }`,
          { variant: 'success' }
        );
      } catch (error) {
        if (rowsRequiringDraftPreparation.length > 0) {
          await queryClient.invalidateQueries({ queryKey: [ANNOTATIONS_BASE_QUERY_KEY] });
        }
        enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      } finally {
        submissionInFlightRef.current = false;
        setPendingTarget(null);
      }
    },
    [annotationAssignmentId, confirmWithModal, mutation, onCompleted, queryClient, selectedRows]
  );

  return {
    submit,
    isPending: mutation.isLoading || submissionInFlightRef.current,
    pendingTarget,
  };
};
